/* Plúr — dot-matrix mark.
   Samples the perforated PLÚR mark (the dot print from the bags) into
   individual dots that behave like flour: they drift in from a scatter,
   part around the pointer and settle back onto the grid.

   <canvas data-dotmark data-src="…plur-mark.png" data-color="--paper"
           data-align="center|right|left" data-intro="scatter|none"></canvas> */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cache = new Map();

  const loadPoints = (src) => {
    if (cache.has(src)) return cache.get(src);
    const p = new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // The source dots sit on an ~8px pitch at 512px; sample on that grid.
        const pitch = img.naturalWidth / 64;
        const cols = Math.round(img.naturalWidth / pitch);
        const rows = Math.round(img.naturalHeight / pitch);
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const g = c.getContext('2d', { willReadFrequently: true });
        g.drawImage(img, 0, 0);
        let data;
        try {
          data = g.getImageData(0, 0, c.width, c.height).data;
        } catch (e) {
          reject(e);
          return;
        }
        const pts = [];
        for (let y = 0; y < rows; y++) {
          for (let x = 0; x < cols; x++) {
            // Look across the cell so a dot that is a little off-centre still counts.
            let a = 0;
            for (let oy = 0; oy < pitch; oy += 2) {
              for (let ox = 0; ox < pitch; ox += 2) {
                const px = Math.floor(x * pitch + ox);
                const py = Math.floor(y * pitch + oy);
                a = Math.max(a, data[(py * c.width + px) * 4 + 3]);
              }
            }
            if (a > 60) pts.push([x / cols, y / rows]);
          }
        }
        resolve({ pts, aspect: img.naturalWidth / img.naturalHeight, cols });
      };
      img.onerror = reject;
      img.src = src;
    });
    cache.set(src, p);
    return p;
  };

  const cssColor = (v) => (v && v.startsWith('--') ? getComputedStyle(document.documentElement).getPropertyValue(v).trim() : v) || '#6e5648';

  async function mount(canvas) {
    const { pts, aspect, cols } = await loadPoints(canvas.dataset.src);
    const ctx = canvas.getContext('2d');
    const color = cssColor(canvas.dataset.color);
    const align = canvas.dataset.align || 'center';
    const intro = canvas.dataset.intro || 'scatter';
    const radiusScale = parseFloat(canvas.dataset.dot || '0.36');
    const n = pts.length;
    const home = new Float32Array(n * 2);
    const pos = new Float32Array(n * 2);
    const vel = new Float32Array(n * 2);
    const seed = new Float32Array(n);
    let w = 0;
    let h = 0;
    let dpr = 1;
    let r = 1;
    let started = false;

    const layout = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      if (!w || !h) return;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      let mw = w;
      let mh = w / aspect;
      if (mh > h) {
        mh = h;
        mw = h * aspect;
      }
      const ox = align === 'right' ? w - mw : align === 'left' ? 0 : (w - mw) / 2;
      const oy = (h - mh) / 2;
      r = (mw / cols) * radiusScale;
      for (let i = 0; i < n; i++) {
        home[i * 2] = ox + pts[i][0] * mw + r;
        home[i * 2 + 1] = oy + pts[i][1] * mh + r;
        if (!started) {
          if (intro === 'scatter' && !reduce) {
            pos[i * 2] = Math.random() * w;
            pos[i * 2 + 1] = Math.random() * h;
          } else {
            pos[i * 2] = home[i * 2];
            pos[i * 2 + 1] = home[i * 2 + 1];
          }
          seed[i] = Math.random();
        }
      }
      started = true;
    };
    layout();
    new ResizeObserver(() => {
      started = true;
      layout();
    }).observe(canvas);

    const mouse = { x: -9999, y: -9999, active: false };
    const target = canvas.closest('[data-dotmark-scope]') || canvas;
    target.addEventListener(
      'pointermove',
      (e) => {
        const rect = canvas.getBoundingClientRect();
        mouse.x = e.clientX - rect.left;
        mouse.y = e.clientY - rect.top;
        mouse.active = true;
      },
      { passive: true }
    );
    target.addEventListener('pointerleave', () => (mouse.active = false));

    let visible = false;
    new IntersectionObserver(([e]) => (visible = e.isIntersecting)).observe(canvas);

    let t = 0;
    let last = performance.now();
    const tick = (now) => {
      requestAnimationFrame(tick);
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      if (!visible || document.hidden || !w) return;
      t += dt;
      const R = Math.max(60, w * 0.09);
      const R2 = R * R;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = color;
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const ix = i * 2;
        const iy = ix + 1;
        let fx = (home[ix] - pos[ix]) * 16;
        let fy = (home[iy] - pos[iy]) * 16;
        if (mouse.active && !reduce) {
          const dx = pos[ix] - mouse.x;
          const dy = pos[iy] - mouse.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < R2) {
            const d = Math.sqrt(d2) || 1;
            const f = (1 - d / R) * 2600;
            fx += (dx / d) * f;
            fy += (dy / d) * f - f * 0.15; // a little lift, like flour puffing up
          }
        }
        vel[ix] = (vel[ix] + fx * dt) * Math.pow(0.02, dt);
        vel[iy] = (vel[iy] + fy * dt) * Math.pow(0.02, dt);
        pos[ix] += vel[ix] * dt;
        pos[iy] += vel[iy] * dt;
        const breathe = reduce ? 1 : 0.85 + 0.15 * Math.sin(t * 1.6 + home[ix] * 0.02 + home[iy] * 0.015);
        const rr = r * breathe * (0.85 + seed[i] * 0.3);
        ctx.moveTo(pos[ix] + rr, pos[iy]);
        ctx.arc(pos[ix], pos[iy], rr, 0, Math.PI * 2);
      }
      ctx.fill();
    };
    requestAnimationFrame(tick);
    canvas.classList.add('is-ready');
  }

  const init = () =>
    document.querySelectorAll('canvas[data-dotmark]').forEach((c) => mount(c).catch((e) => console.warn('[plur] dotmark:', e)));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
